import {
  CheckCertificateRevocationResources,
  ERRORS,
  MergedSourceResources,
  SERVER_ERRORS,
  type RequestPermissionRequest,
  type SDKInstance,
  checkCertificateRevocation,
  closeWindow,
  createApiBackend,
  createAppBackend,
  createSDKInstance,
  isMethodSupported,
  onError,
  openReregistration,
  openResourceEditor,
  openServiceSettings,
  readResourceRaw,
  readResourceWithSignature,
  readResourcesRaw,
  readWithQrScanner,
  requestAppUpdate,
  requestPermission,
} from "@pocketsign/in-app-sdk";
import { requestSubscriptionIdWithSignatureAsAgent } from "@pocketsign/in-app-sdk/internal";
import * as Sentry from "@sentry/react";
import {
  RESIDENCE_CARD_CAPABILITY_RESOURCE_ID,
  RESIDENCE_CARD_EXPIRY_DATE_RESOURCE_ID,
  birthdayResourceId,
  getProfileResourceIds,
  getSubscriptionIdResourceId,
} from "../config";
import type { TenantWithFeature } from "./connect";
import { createJSTDate, formatDate } from "./date";
import { AppError, ERROR_CODES } from "./errors";
import { t } from "./i18n";
import { flushSentry } from "./sentry";
import { isMockEnv } from "./utils";
export let sdk: SDKInstance;

type SDKResult = {
  result: string;
  errno?: number;
};

type ReportSDKResultOptions<T> = {
  ignoredErrnos?: readonly number[];
  // errno だけでは想定内か決まらないもの（SERVER_ERROR の data.type など）を除外する
  isExpectedError?: (result: T) => boolean;
};

export async function reportSDKResult<T extends SDKResult>(
  operation: string,
  call: () => Promise<T>,
  options?: ReportSDKResultOptions<T>,
): Promise<T> {
  const result = await call();

  if (
    result.result === "error" &&
    result.errno !== undefined &&
    result.errno !== ERRORS.NETWORK_ERROR &&
    !options?.ignoredErrnos?.includes(result.errno) &&
    !options?.isExpectedError?.(result)
  ) {
    Sentry.captureException(
      new Error(`In-App SDK ${operation} failed: ${result.errno}`),
      {
        tags: {
          sdkOperation: operation,
          sdkErrno: String(result.errno),
        },
      },
    );
  }

  return result;
}

export async function closeMiniAppWindow(instance: SDKInstance) {
  await flushSentry();
  return reportSDKResult("closeWindow", () => closeWindow(instance));
}

export function shouldSkipMockSDK() {
  return isMockEnv() && import.meta.env.VITE_MOCK_SKIP_SDK === "true";
}

export async function initSDK(serviceId: string) {
  if (sdk) {
    return sdk;
  }

  if (shouldSkipMockSDK()) {
    sdk = {} as SDKInstance;
    return sdk;
  }

  const backend =
    import.meta.env.VITE_P8N_BACKEND_MODE === "API"
      ? createApiBackend({
          accessToken: import.meta.env.VITE_P8N_API_ACCESS_TOKEN,
          baseUrl: "https://internal.core.mock.p8n.dev",
        })
      : createAppBackend();

  sdk = await createSDKInstance({
    serviceId,
    backend,
  });

  onError(sdk, (type, message, error) => {
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(`${type}: ${message}`, {
        level: "error",
      });
    }
  });

  if (
    !isMethodSupported(sdk, readWithQrScanner) ||
    !isMethodSupported(sdk, readResourceWithSignature) ||
    !isMethodSupported(sdk, requestPermission) ||
    !isMethodSupported(sdk, openReregistration)
  ) {
    window.alert(
      t(
        "アプリの更新が必要です。更新を適用するため、アプリの再読み込みまたはストアへの移動を行います。",
      ),
    );
    const result = await reportSDKResult("requestAppUpdate", () =>
      requestAppUpdate(sdk),
    );

    if (result.result === "success") {
      throw new AppError(ERROR_CODES.SDK_METHOD_NOT_SUPPORTED);
    }
  }

  return sdk;
}

const RESIDENCE_CARD_PERMISSION_FALLBACK_KEY =
  "residence-card-permission-fallback-reported";

export async function requestPermissions(tenant: TenantWithFeature) {
  const profileResourceIds = getProfileResourceIds(tenant.slug);
  const subscriptionIdResourceId = getSubscriptionIdResourceId(tenant.slug);

  const permissionRequests: RequestPermissionRequest = [
    {
      resourceId: subscriptionIdResourceId,
      verbs: [{ verb: "write" as const, term: "always" as const }],
    },
    ...profileResourceIds.map((resourceId) => ({
      resourceId,
      verbs: [{ verb: "read" as const, term: "always" as const }],
    })),
  ];

  // miyagiテナントの場合のみ、署名用証明書チェックの権限を追加
  if (tenant.slug === "miyagi") {
    permissionRequests.push({
      resourceId: CheckCertificateRevocationResources.jpkiCardDigitalSignature,
      verbs: [{ verb: "invoke" as const, term: "always" as const }],
    });
    // fund source の RequiredCertificateAnyOf でスマホ用署名用証明書も受け付けるため、
    // バックエンドが KLON 経由で確認できるよう権限を取っておく
    permissionRequests.push({
      resourceId:
        CheckCertificateRevocationResources.jpkiMobileDigitalSignature,
      verbs: [{ verb: "invoke" as const, term: "always" as const }],
    });
    // ようこそみやぎ外国人ポイントのバナー表示判定に使う
    permissionRequests.push({
      resourceId: RESIDENCE_CARD_CAPABILITY_RESOURCE_ID,
      verbs: [{ verb: "read" as const, term: "always" as const }],
    });
  }

  let permission = await requestPermission(sdk, permissionRequests);

  // 搭載状況・スマホ用署名用証明書の権限要求が p8n 側で許可されていない環境でもログインを壊さない。
  // requestPermission はバッチ単位で失敗するため、未設定のままデプロイすると
  // miyagi 全ユーザーがログインできなくなる。このフォールバックが動いた環境ではバナーは出ず、
  // スマホ用署名用証明書しか持たないユーザーは証明書必須の fund source を受け取れない。
  //
  // TODO: dev-mock / prod-mock / prod-live の3環境すべてで KlonGrantRequestablePermission が
  // 完了したら、このフォールバックごと削除する。想定される定常状態ではない
  if (permission.result === "error" && tenant.slug === "miyagi") {
    // 発火中は全ユーザーの毎ログインで通るため、セッション1回に絞って Sentry を溢れさせない
    if (!sessionStorage.getItem(RESIDENCE_CARD_PERMISSION_FALLBACK_KEY)) {
      sessionStorage.setItem(RESIDENCE_CARD_PERMISSION_FALLBACK_KEY, "1");
      Sentry.captureMessage(
        // errno を本文に埋めると errno ごとに issue が分かれるため tag に回す
        "requestPermission failed; retrying without optional miyagi permissions",
        { level: "warning", tags: { sdkErrorNo: String(permission.errno) } },
      );
    }
    const optionalResourceIds: string[] = [
      RESIDENCE_CARD_CAPABILITY_RESOURCE_ID,
      CheckCertificateRevocationResources.jpkiMobileDigitalSignature,
    ];
    permission = await requestPermission(
      sdk,
      permissionRequests.filter(
        (request) => !optionalResourceIds.includes(request.resourceId),
      ),
    );
  }

  if (permission.result === "cancel" || permission.result === "disagree") {
    throw new AppError(ERROR_CODES.SDK_PERMISSION_NOT_ACCEPTED, undefined, {
      skipSentry: true,
    });
  }

  if (permission.result === "error") {
    throw new AppError(ERROR_CODES.SDK_PERMISSION_ERROR, undefined, {
      sdkErrorNo: permission.errno,
    });
  }

  return permission;
}

export async function getCityAddressWithCache(): Promise<string | null> {
  const mockCityAddress = import.meta.env.VITE_MOCK_CITY_ADDRESS;
  if (mockCityAddress) {
    return mockCityAddress;
  }

  const sessionKey = "city-address";
  const cache = sessionStorage.getItem(sessionKey);

  if (cache) {
    return cache;
  }

  const response = await reportSDKResult("readResourceRaw", () =>
    readResourceRaw(sdk, {
      resourceId: MergedSourceResources.cityAddress,
    }),
  );

  if (response.result !== "success") {
    return null;
  }

  if (!response.value) {
    return null;
  }

  const cityAddress = JSON.parse(response.value) as string;

  sessionStorage.setItem(sessionKey, cityAddress);

  return cityAddress;
}

export async function getBillingProfileWithCache(): Promise<{
  fullName: string;
  cityAddress: string;
}> {
  const mockCityAddress = import.meta.env.VITE_MOCK_CITY_ADDRESS;
  if (mockCityAddress) {
    return {
      fullName: "",
      cityAddress: mockCityAddress,
    };
  }

  const sessionKey = "billing-profile";
  const cache = sessionStorage.getItem(sessionKey);

  if (cache) {
    return JSON.parse(cache) as {
      fullName: string;
      cityAddress: string;
    };
  }

  const response = await reportSDKResult("readResourcesRaw", () =>
    readResourcesRaw(sdk, {
      resourceIds: [
        MergedSourceResources.fullName,
        MergedSourceResources.cityAddress,
      ],
    }),
  );

  if (response.result !== "success") {
    return {
      fullName: "",
      cityAddress: "",
    };
  }

  const profile = {
    fullName:
      parseResourceValue(
        response.values.find(
          (value) => value.id === MergedSourceResources.fullName,
        )?.value,
      ) ?? "",
    cityAddress:
      parseResourceValue(
        response.values.find(
          (value) => value.id === MergedSourceResources.cityAddress,
        )?.value,
      ) ?? "",
  };

  sessionStorage.setItem(sessionKey, JSON.stringify(profile));

  return profile;
}

export async function openUserAuthenticationReregistration() {
  return reportSDKResult("openReregistration", () =>
    openReregistration(sdk, { type: "user_authentication" }),
  );
}

// 英数字6〜16桁の暗証番号で、署名用電子証明書による登録をアプリ側で行ってもらう
export async function openDigitalSignatureReregistration(): Promise<
  "success" | "cancel" | "error"
> {
  if (shouldSkipMockSDK()) {
    return "success";
  }

  const result = await reportSDKResult("openReregistration", () =>
    openReregistration(sdk, { type: "digital_signature" }),
  );
  return result.result;
}

export async function requestRepresentedSubscriptionIdWithSignatureAsAgent(): Promise<string> {
  if (shouldSkipMockSDK()) {
    return "mock-represented-subscription-id-jwt";
  }

  if (!isMethodSupported(sdk, requestSubscriptionIdWithSignatureAsAgent)) {
    window.alert(
      t(
        "アプリの更新が必要です。更新を適用するため、アプリの再読み込みまたはストアへの移動を行います。",
      ),
    );
    const result = await reportSDKResult("requestAppUpdate", () =>
      requestAppUpdate(sdk),
    );

    if (result.result === "success") {
      throw new AppError(ERROR_CODES.SDK_METHOD_NOT_SUPPORTED);
    }
  }

  const result = await reportSDKResult(
    "requestSubscriptionIdWithSignatureAsAgent",
    () => requestSubscriptionIdWithSignatureAsAgent(sdk),
  );

  if (result.result === "cancel") {
    throw new AppError(
      ERROR_CODES.SDK_PERMISSION_NOT_ACCEPTED,
      t("マイナンバーカードの読み取りがキャンセルされました"),
      { skipSentry: true },
    );
  }

  if (result.result === "error") {
    throw new AppError(
      ERROR_CODES.FAILED_TO_GET_REPRESENTED_SUBSCRIPTION_ID,
      undefined,
      {
        sdkErrorNo: result.errno,
      },
    );
  }

  return result.subscriptionId;
}

export async function getCityAddressWithUpdatedAt(): Promise<{
  cityAddress: string | null;
  updatedAt: Date | null;
}> {
  const mockCityAddress = import.meta.env.VITE_MOCK_CITY_ADDRESS;
  if (mockCityAddress) {
    return { cityAddress: mockCityAddress, updatedAt: new Date() };
  }

  const response = await reportSDKResult("readResourcesRaw", () =>
    readResourcesRaw(sdk, {
      resourceIds: [MergedSourceResources.cityAddress],
    }),
  );
  if (response.result !== "success" || response.values.length === 0) {
    return { cityAddress: null, updatedAt: null };
  }
  const value = response.values[0];
  return {
    cityAddress: value.value ? (JSON.parse(value.value) as string) : null,
    updatedAt: value.updatedAt,
  };
}

// invalid は未登録（4桁のみでの登録を含む）か失効で、署名用での登録をやり直せば解消する。
// 有効期限切れは valid で返るので invalid にならない。
// unavailable は通信・サーバー内部エラーで、時間をおけば解消しうる。
// error はそれ以外で、再試行でも登録のやり直しでも解消しない
export type DigitalSignatureCertificateStatus =
  | "valid"
  | "invalid"
  | "unavailable"
  | "error";

type CheckCertificateRevocationResult = Awaited<
  ReturnType<typeof checkCertificateRevocation>
>;

// 未登録は success ではなく、Core が FailedPrecondition を返して SERVER_ERROR になる
function isCertificateNotRegistered(result: CheckCertificateRevocationResult) {
  return (
    result.result === "error" &&
    result.errno === ERRORS.SERVER_ERROR &&
    result.data.type === SERVER_ERRORS.FAILED_PRECONDITION
  );
}

export async function getDigitalSignatureCertificateStatus(): Promise<DigitalSignatureCertificateStatus> {
  const mockStatus = import.meta.env.VITE_MOCK_DIGITAL_SIGNATURE_STATUS;
  if (mockStatus) {
    return mockStatus as DigitalSignatureCertificateStatus;
  }

  if (shouldSkipMockSDK()) {
    return "valid";
  }

  const result = await reportSDKResult(
    "checkCertificateRevocation",
    () =>
      checkCertificateRevocation(sdk, {
        type: "jpki_card_digital_signature",
      }),
    // 4桁のみで登録した大多数の人が該当し、ホームを開くたびに通る
    { isExpectedError: isCertificateNotRegistered },
  );
  if (result.result === "success") {
    return result.valid ? "valid" : "invalid";
  }
  if (isCertificateNotRegistered(result)) {
    return "invalid";
  }
  if (
    result.errno === ERRORS.NETWORK_ERROR ||
    result.errno === ERRORS.SERVER_INTERNAL_ERROR
  ) {
    return "unavailable";
  }
  return "error";
}

export async function checkDigitalSignatureCertificate(): Promise<boolean> {
  return (await getDigitalSignatureCertificateStatus()) === "valid";
}

export async function getBirthday(): Promise<Date | null> {
  const sessionKey = "birthday";
  const cache = sessionStorage.getItem(sessionKey);

  if (cache) {
    return new Date(cache);
  }

  const response = await reportSDKResult("readResourceRaw", () =>
    readResourceRaw(sdk, {
      resourceId: birthdayResourceId,
    }),
  );

  if (response.result !== "success") {
    return null;
  }

  if (!response.value) {
    return null;
  }

  const birthday = JSON.parse(response.value) as string;

  sessionStorage.setItem(sessionKey, birthday);

  return new Date(birthday);
}

export type ResidenceCardCapability = "present" | "absent";
export type ResidenceCardCapabilityResult =
  | ResidenceCardCapability
  | null
  | "unavailable";

// null は正常に取得できた未判定の値。権限不足・取得失敗は再認証では解消しないため区別する。
export async function getResidenceCardCapability(): Promise<ResidenceCardCapabilityResult> {
  const mockCapability = import.meta.env.VITE_MOCK_RESIDENCE_CARD_CAPABILITY;
  if (mockCapability) {
    return mockCapability as ResidenceCardCapability;
  }

  if (shouldSkipMockSDK()) {
    // 同意・カード読み取りと同じく、SDK を介さないモック環境では搭載済みとして扱う
    return "present";
  }

  const response = await readResourceRaw(sdk, {
    resourceId: RESIDENCE_CARD_CAPABILITY_RESOURCE_ID,
  });

  if (response.result !== "success") {
    return "unavailable";
  }

  if (!response.value) {
    return null;
  }

  // リソース値は JSON とは限らない。ここで throw するとホームのローダーごと落ちる
  const capability = parseResourceValue(response.value);
  if (capability === "present" || capability === "absent") {
    return capability;
  }
  return "unavailable";
}

// ようこそみやぎ外国人ポイント: 在留カード情報の取得に関する同意画面を表示する
export async function requestResidenceCardPermissions(): Promise<
  "agree" | "cancel"
> {
  if (shouldSkipMockSDK()) {
    return "agree";
  }

  const permission = await requestPermission(sdk, [
    {
      resourceId: RESIDENCE_CARD_EXPIRY_DATE_RESOURCE_ID,
      verbs: [{ verb: "read" as const, term: "always" as const }],
    },
  ]);

  if (permission.result === "cancel" || permission.result === "disagree") {
    return "cancel";
  }

  if (permission.result === "error") {
    throw new AppError(ERROR_CODES.SDK_PERMISSION_ERROR, undefined, {
      sdkErrorNo: permission.errno,
    });
  }

  return "agree";
}

// 特定在留カード等の登録（暗証番号・在留カード等番号の入力とカード読み取り）をアプリ側で開く
export async function openResidenceCardRegistration(): Promise<
  "success" | "cancel"
> {
  if (shouldSkipMockSDK()) {
    return "success";
  }

  if (!isMethodSupported(sdk, openResourceEditor)) {
    window.alert(
      t(
        "アプリの更新が必要です。更新を適用するため、アプリの再読み込みまたはストアへの移動を行います。",
      ),
    );
    // 更新に失敗しても未対応のままなので、いずれの結果でも呼び出しには進ませない
    await requestAppUpdate(sdk);
    throw new AppError(ERROR_CODES.SDK_METHOD_NOT_SUPPORTED);
  }

  const result = await openResourceEditor(sdk, {
    resourceId: RESIDENCE_CARD_EXPIRY_DATE_RESOURCE_ID,
  });

  if (result.result === "cancel") {
    return "cancel";
  }

  if (result.result === "error") {
    throw new AppError(
      ERROR_CODES.RESIDENCE_CARD_REGISTRATION_FAILED,
      undefined,
      {
        sdkErrorNo: result.errno,
      },
    );
  }

  return "success";
}

const MOCK_RESIDENCE_CARD_EXPIRES_AT = new Date("2099-12-31T00:00:00+09:00");

// 特定在留カード等が失効する日時。カードは有効期間満了日の終わりまで有効なので満了日の翌日 0:00 (JST) を返す。
// 未同意・未登録・読み取り失敗・値が不正な場合は null
export async function getResidenceCardExpiresAt(): Promise<Date | null> {
  const mockExpiryDate = import.meta.env.VITE_MOCK_RESIDENCE_CARD_EXPIRY_DATE;
  if (mockExpiryDate) {
    return parseResidenceCardExpiresAt(mockExpiryDate);
  }

  if (shouldSkipMockSDK()) {
    // 同意・カード読み取りと同じく、SDK を介さないモック環境では成功として扱う
    return MOCK_RESIDENCE_CARD_EXPIRES_AT;
  }

  const response = await readResourceRaw(sdk, {
    resourceId: RESIDENCE_CARD_EXPIRY_DATE_RESOURCE_ID,
  });

  if (response.result !== "success" || !response.value) {
    return null;
  }

  const value = parseResourceValue(response.value);
  if (value === undefined) {
    return null;
  }
  return parseResidenceCardExpiresAt(value);
}

// リソースの値は JST の暦日を表す「YYYYMMDD」
function parseResidenceCardExpiresAt(value: string): Date | null {
  const matched = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
  if (!matched) {
    return null;
  }

  const [, year, month, day] = matched;
  const expiryDate = createJSTDate(`${year}-${month}-${day}`);
  if (Number.isNaN(expiryDate.getTime())) {
    return null;
  }
  // JST に夏時間はないので、24 時間加算すれば満了日の翌日 0:00 になる
  return new Date(expiryDate.getTime() + 24 * 60 * 60 * 1000);
}

export type SettingsProfile = {
  name: string;
  address: string;
  gender: string;
  birthday: string;
};

function formatGender(value: string | undefined): string {
  // 性別リソースの生値は "male" / "female"。それ以外は「その他」に寄せる
  switch (value) {
    case "male":
      return t("男性");
    case "female":
      return t("女性");
    default:
      return t("その他");
  }
}

function formatBirthday(value: string | undefined): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return formatDate(date, "date");
}

// 設定画面で表示する本人情報（氏名・住所・性別・生年月日）を SDK から取得する
export async function getSettingsProfile(): Promise<SettingsProfile> {
  if (shouldSkipMockSDK()) {
    return {
      name: "春日 太郎",
      address: "東京都中央区日本橋室町三丁目",
      // 実データ経路と同じ整形を通す。ここで整形済みの文字列を返すと mock 環境だけロケールが効かない
      gender: formatGender("male"),
      birthday: formatBirthday("2000-03-10"),
    };
  }

  const response = await readResourcesRaw(sdk, {
    resourceIds: [
      MergedSourceResources.fullName,
      MergedSourceResources.fullAddress,
      MergedSourceResources.gender,
      MergedSourceResources.birthDate,
    ],
  });

  if (response.result !== "success") {
    throw new AppError(ERROR_CODES.PROFILE_READ_FAILED);
  }

  const find = (id: string) =>
    parseResourceValue(response.values.find((value) => value.id === id)?.value);

  return {
    name: find(MergedSourceResources.fullName) ?? "",
    address: find(MergedSourceResources.fullAddress) ?? "",
    gender: formatGender(find(MergedSourceResources.gender)),
    birthday: formatBirthday(find(MergedSourceResources.birthDate)),
  };
}

// ミニアプリ詳細（お問い合わせ先など）をネイティブのサービス設定画面で開く
export async function openMiniAppServiceSettings() {
  if (shouldSkipMockSDK()) {
    window.alert(
      t("ミニアプリ詳細を開きます（モック環境のため画面遷移は行われません）"),
    );
    return;
  }

  await reportSDKResult("openServiceSettings", () => openServiceSettings(sdk));
}

function parseResourceValue(value: string | null | undefined) {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value) as string;
  } catch {
    return value;
  }
}
